import React from 'react';
import PropTypes from 'prop-types';

// Common components
import Slider from '../common/Slider/Slider';

// Component styles
import './fontPreviewToolbar.scss';

/**
 * This component shows the FontPreviewToolbar.
 * - It exposes the input and slider changes via callbacks.
 * - It shows a default text in the input text if it is defined.
 * - It exposes the slider on mouse leave value via callback.
 * @extends {Component}
 */
class FontPreviewToolbar extends React.Component {
  constructor(props) {
    super(props);

    /**
     * Component initial state.
     * @type {Object}
     * @property {String} inputText It handles the state of the input. It will take the default value from the text
     *                              props or an empty string.
     */
    this.state = {
      inputText: this.props.text,
    };

    // Context binding for function.
    this.updateInputValue = this.updateInputValue.bind(this);
    this.onChangeButtonClick = this.onChangeButtonClick.bind(this);
  }

  /**
   * After an update occurs, it will check if the text prop is different from the previous
   * text prop value in order to update its status.
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    if (this.props.text !== prevProps.text) {
      this.updateInputTextState();
    }
  }

  /**
   * When the Change button is clicked, `onChangeButtonClick` prop is executed with the input text value.
   */
  onChangeButtonClick() {
    this.props.onChangeButtonClick(this.state.inputText);
  }

  /**
   * @returns an object with all the slider props.
   */
  getSliderProps() {
    return {
      value: this.props.sliderValue,
      onSliderChange: this.props.onSliderChange,
      onSliderMouseLeave: this.props.onSliderMouseLeave,
      minRange: this.props.sliderMinRange,
      maxRange: this.props.sliderMaxRange,
    };
  }

  /**
   * This method updates the `inputText` state.
   * @param {Object} event slider onChange event.
   */
  updateInputValue(event) {
    this.setState({
      inputText: event.target.value,
    });
  }

  /**
   * This method is executed after an update occurs in the component and the inputText previous prop
   * differs from the current one.
   */
  updateInputTextState() {
    this.setState({
      inputText: this.props.text,
    });
  }

  render() {
    const isDisabled = !this.state.inputText.trim();
    const disabledClassModifier = isDisabled ? '-disabled' : '';

    return (
      <div className='wtfFontPreviewToolbar'>
        <input
          className='wtfFontPreviewToolbar_input'
          value={this.state.inputText}
          onChange={this.updateInputValue}
          placeholder='Enter your own text'
        />
        <button
          disabled={isDisabled}
          onClick={this.onChangeButtonClick}
          onKeyPress={this.onChangeButtonClick}
          className={`wtfFontPreviewToolbar_changeButton ${disabledClassModifier}`}
        >
          Change Text
        </button>
        <div className='wtfFontPreviewToolbar_sizeSlider'>
          <Slider {...this.getSliderProps()} />
        </div>
      </div>
    );
  }
}

/**
 * Set the FontPreviewToolbar component props default values.
 * @type {Object}
 * @property {String}   text                It sets the default value of the input text.
 * @property {String}   sliderValue         It sets the default value of the slider component.
 * @property {String}   sliderMinRange      It sets the min range for the slider component.
 * @property {String}   sliderMaxRange      It sets the max range for the slider component.
 * @property {Function} onChangeButtonClick Callback that is executed when the Change button is clicked.
 * @property {Function} onSliderChange      Callback that is executed on slider on change.
 * @property {Function} onSliderMouseLeave  Callback that is executed on slider mouse leave.
 */
FontPreviewToolbar.defaultProps = {
  text: '',
  sliderValue: '72',
  sliderMinRange: '8',
  sliderMaxRange: '240',
  onChangeButtonClick: () => {},
  onSliderChange: () => {},
  onSliderMouseLeave: () => {},
};

/**
 * Declare the FontPreviewToolbar component PropTypes for better validation.
 * @type {Object}
 */
FontPreviewToolbar.propTypes = {
  text: PropTypes.string,
  sliderValue: PropTypes.string,
  sliderMinRange: PropTypes.string,
  sliderMaxRange: PropTypes.string,
  onChangeButtonClick: PropTypes.func,
  onSliderChange: PropTypes.func,
  onSliderMouseLeave: PropTypes.func,
};

export default FontPreviewToolbar;



// WEBPACK FOOTER //
// ./node_modules/myfonts-font-preview-toolbar/src/components/FontPreviewToolbar/FontPreviewToolbar.js